import { useState, useEffect } from "react";
import { X, Sparkles, Tag, ArrowRight, Copy, Check } from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

const PromoBanner = () => {
  const navigate = useNavigate();
  const [promotion, setPromotion] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const fetchActivePromotion = async () => {
      try {
        // Check if user has dismissed this promotion
        const dismissedPromos = JSON.parse(localStorage.getItem('dismissedPromos') || '[]');
        
        const response = await axios.get(`${API}/api/coupons/promotions/active`);
        const activePromos = response.data || [];
        
        if (activePromos.length > 0) {
          // Find the first promotion that hasn't been dismissed
          const activePromo = activePromos.find(p => !dismissedPromos.includes(p.id));
          if (activePromo) {
            setPromotion(activePromo);
            // Store the coupon code in sessionStorage for auto-apply
            if (activePromo.coupon_code) {
              sessionStorage.setItem('promoCoupon', activePromo.coupon_code);
            }
          }
        }
      } catch (error) {
      } finally {
        setLoading(false);
      }
    };

    fetchActivePromotion();
  }, []);

  const handleDismiss = () => {
    if (promotion) {
      // Store dismissed promotion ID in localStorage
      const dismissedPromos = JSON.parse(localStorage.getItem('dismissedPromos') || '[]');
      dismissedPromos.push(promotion.id);
      localStorage.setItem('dismissedPromos', JSON.stringify(dismissedPromos));
    }
    setDismissed(true);
  };

  const handleBookNow = () => {
    // Navigate to estimate page with coupon code
    if (promotion?.coupon_code) {
      navigate(`/get-estimate?promo=${promotion.coupon_code}`);
    } else {
      navigate("/get-estimate");
    }
  };

  const handleCopyCode = async () => {
    if (promotion?.coupon_code) {
      try {
        await navigator.clipboard.writeText(promotion.coupon_code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error("Failed to copy");
      }
    }
  };

  const getDiscountLabel = () => {
    if (!promotion) return "";
    if (promotion.discount_type === "percentage") {
      return `${promotion.discount_value}% OFF`;
    }
    return `$${promotion.discount_value} OFF`;
  };

  const getExpiryText = () => {
    if (!promotion?.end_date) return "";
    const endDate = new Date(promotion.end_date);
    const now = new Date();
    const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysLeft <= 0) return "";
    if (daysLeft === 1) return "Ends today!";
    if (daysLeft <= 3) return `Only ${daysLeft} days left!`;
    if (daysLeft <= 7) return `Ends in ${daysLeft} days`;
    return "";
  };

  // Don't render if loading, dismissed, or no promotion
  if (loading || dismissed || !promotion) {
    return null;
  }

  const expiryText = getExpiryText();

  return (
    <div 
      className="relative bg-gradient-to-r from-orange-600 via-orange-500 to-amber-500 text-white overflow-hidden"
      data-testid="promo-banner"
    >
      {/* Animated background pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }} />
      </div>
      
      <div className="container mx-auto px-4 py-2.5 sm:py-3 relative">
        <div className="flex items-center justify-center gap-2 sm:gap-4">
          {/* Sparkle icon */}
          <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-300 animate-pulse flex-shrink-0" />
          
          {/* Banner content */}
          <div className="flex flex-col sm:flex-row items-center gap-1 sm:gap-3 text-center sm:text-left">
            {/* Discount badge */}
            <span className="inline-flex items-center gap-1 bg-white/20 backdrop-blur-sm px-2.5 py-0.5 rounded-full text-xs sm:text-sm font-bold">
              <Tag className="w-3 h-3" />
              {getDiscountLabel()}
            </span>
            
            {/* Promotion text */}
            <span className="text-sm sm:text-base font-medium">
              {promotion.banner_text || promotion.name}
            </span>
            
            {/* Coupon code badge - Clickable to copy */}
            {promotion.coupon_code && (
              <button
                onClick={handleCopyCode}
                className="hidden sm:inline-flex items-center gap-1.5 bg-white/30 backdrop-blur-sm px-2.5 py-1 rounded-md text-xs font-mono font-bold hover:bg-white/40 transition-colors"
                title="Click to copy code"
                data-testid="promo-coupon-code"
              >
                {copied ? (
                  <>
                    <Check className="w-3 h-3 text-green-300" />
                    <span>Copied!</span>
                  </>
                ) : (
                  <>
                    <span>Code: {promotion.coupon_code}</span>
                    <Copy className="w-3 h-3 opacity-70" />
                  </>
                )}
              </button>
            )}
            
            {/* Expiry urgency text */}
            {expiryText && (
              <span className="hidden lg:inline-flex text-xs text-yellow-200 font-medium animate-pulse">
                {expiryText}
              </span>
            )}
            
            {/* CTA Button */}
            <button
              onClick={handleBookNow}
              className="hidden sm:inline-flex items-center gap-1 bg-white text-orange-600 px-3 py-1 rounded-full text-sm font-semibold hover:bg-orange-50 transition-colors"
              data-testid="promo-banner-cta"
            >
              Book Now
              <ArrowRight className="w-3 h-3" />
            </button>
          </div>
          
          {/* Sparkle icon (right side) */}
          <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-yellow-300 animate-pulse flex-shrink-0 hidden sm:block" />
          
          {/* Dismiss button */}
          <button
            onClick={handleDismiss}
            className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 p-1 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Dismiss banner"
            data-testid="promo-banner-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default PromoBanner;
